declare global {
    interface Window {
        dataLayer?: unknown[]
    }
}

/**
 * The Google Ads Enhanced Conversions match signal, in Google's pre-hashed shape:
 * the `awec` variable reads `sha256_email_address` at the top level and the name
 * under `address`, beside the postal fields. A name placed at the top level is not
 * an error to the variable; it is simply not read, and the conversion carries an
 * email-only match with nothing to show that the name was dropped.
 *
 * Pre-hashed because our tag points at the server container: in that mode
 * `gtag.js` does not hash in the browser, so a plaintext key would travel to sGTM
 * as itself and appear in its preview output and ingress logs.
 */
export interface GtmUserData {
    address?: {
        sha256_first_name?: string
        sha256_last_name?: string
    }
    sha256_email_address: string
}

/**
 * Announces that the identity is on the queue, so a tag can fire at a moment when
 * it is actually there. Setting the key alone produces no hit: the page's tags run
 * at container load, long before a session resolves and a digest lands, and a tag
 * that fired then would send an empty value — which is the destination's signature
 * for "identity was not available" rather than for "no identity".
 *
 * Not a Google recommended event name on purpose. `login` would be the tempting
 * one and would be wrong: this announces a report, which happens on every document
 * a session survives into, not the act of logging in.
 */
const USER_DATA_READY_EVENT = 'user_data_ready'

/**
 * Retract the identity: a tag reads the accumulated data model, not the push, so
 * until something clears the key the last-reported visitor is what any later tag
 * would carry.
 *
 * Announces nothing. A retraction sets the key empty, and a hit sent at that
 * moment would carry an empty value — reported as identity-unavailable rather
 * than as identity-withdrawn.
 *
 * Creating the queue here is load-bearing: a push that lands before the container
 * arrives is replayed by the bootstrap, so an early report is not lost.
 */
export function clearGtmUserData(): void {
    window.dataLayer = window.dataLayer ?? []
    window.dataLayer.push({ user_data: null })
}

/**
 * Clearing first is what makes this a replace. The data model merges an object
 * push field by field, so a report carrying no name would otherwise inherit the
 * previous one — pairing one visitor's email with another's hashed name, which
 * both the audience path and the Events API would read as a single person.
 *
 * **Deliberately does not catch.** GTM replaces `dataLayer.push` with its own
 * function once the container loads, so a container-side error surfaces here, and
 * the policy for it belongs to the caller.
 */
export function pushGtmUserData(userData: GtmUserData): void {
    clearGtmUserData()
    window.dataLayer?.push({ user_data: userData })
    window.dataLayer?.push({ event: USER_DATA_READY_EVENT })
}
